/**
 * Company-arranged rules for outstation travel.
 *
 * A head can be company arranged ONLY if the policy for the tier allows it
 * (head.companyArranged === true). The employee then chooses per request
 * whether to use it. The choice is stored on the request as
 * { [headId]: boolean }.
 *
 * The employee may file expenses only against heads that are NOT company
 * arranged for this request (policy says no, or he toggled it off).
 */

const idOf = (head) => head.headId ?? head.expenseId;

/** Heads for which the policy allows the employee to choose company arranged. */
export const getCompanyArrangeableHeads = (policyHeads = []) =>
  policyHeads.filter((head) => head.companyArranged === true);

/** Backend sends a { "7": true } map; tolerate anything else as "none". */
export const normalizeArrangedMap = (map) => {
  if (!map || typeof map !== 'object') return {};
  return Object.fromEntries(
    Object.entries(map).map(([key, value]) => [String(key), value === true])
  );
};

export const isHeadCompanyArranged = (head, arrangedMap) =>
  head.companyArranged === true &&
  normalizeArrangedMap(arrangedMap)[String(idOf(head))] === true;

/** Heads the employee is allowed to raise expenses against. */
export const getEligibleExpenseHeads = (policyHeads = [], arrangedMap) =>
  policyHeads.filter((head) => !isHeadCompanyArranged(head, arrangedMap));

/**
 * Payload map for the API: only policy-allowed heads, true/false for each.
 * Anything the policy doesn't allow is never sent as true.
 */
export const buildArrangedPayload = (policyHeads = [], arrangedMap) => {
  const map = normalizeArrangedMap(arrangedMap);
  return Object.fromEntries(
    getCompanyArrangeableHeads(policyHeads).map((head) => [
      String(idOf(head)),
      map[String(idOf(head))] === true,
    ])
  );
};
